import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormIssue } from '@interfaces/form-issue';

export type TapAlertType = 'warning' | 'error' | 'info' | 'success';

const ICON_MAP: Record<TapAlertType, string> = {
  warning: 'warning',
  error: 'error',
  info: 'info',
  success: 'check_circle',
};

@Component({
  selector: 'app-tap-alert',
  templateUrl: './tap-alert.component.html',
  styleUrls: ['./tap-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TapAlertComponent {
  @Input() type: TapAlertType = 'warning';
  @Input() title: string;
  @Input() message: string;

  /** Interpolation values for {@link message}, for a warning that has to carry a count or a name. */
  @Input() messageParams: Record<string, unknown>;
  /**
   * Translation KEYS, one per bullet - each is put through the translate pipe. Passing
   * already-translated text here sends that text back through the lookup, misses, and renders it
   * wrapped in square brackets by CommonMissingTranslationHandler. Use {@link linkedItems} for a
   * key that needs interpolation values.
   */
  @Input() items: string[] = [];

  /**
   * Bullets that carry their own interpolation values, and optionally focus a field when clicked.
   * Without a `fieldId` an entry renders as plain text, which is also how to show a key + params
   * bullet that links nowhere.
   */
  @Input() linkedItems: FormIssue[] = [];
  @Input() dismissible = false;

  /**
   * One line - icon, message, projected content, dismiss - for a page-level strip such as a
   * banner. Takes {@link message} and projected content only; {@link items} and
   * {@link linkedItems} need the stacked layout and are not shown.
   */
  @Input() compact = false;
  @Output() dismissed = new EventEmitter<void>();
  @Output() itemFocused = new EventEmitter<string>();

  onItemClick(fieldId: string | undefined): void {
    if (fieldId) {
      this.itemFocused.emit(fieldId);
    }
  }

  get iconName(): string {
    return ICON_MAP[this.type] ?? 'warning';
  }

  onDismiss(): void {
    this.dismissed.emit();
  }
}
